import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ChartToolbar = ({ onToolSelect, selectedTool, onIndicatorAdd, onChartTypeChange, selectedChartType }) => {
  const [expandedSections, setExpandedSections] = useState({
    drawing: true,
    indicators: false,
    chartTypes: false
  });

  const drawingTools = [
    { id: 'cursor', name: 'Cursor', icon: 'MousePointer' },
    { id: 'line', name: 'Trend Line', icon: 'TrendingUp' },
    { id: 'horizontal', name: 'Horizontal Line', icon: 'Minus' },
    { id: 'vertical', name: 'Vertical Line', icon: 'Separator' },
    { id: 'rectangle', name: 'Rectangle', icon: 'Square' },
    { id: 'fibonacci', name: 'Fibonacci', icon: 'GitBranch' },
    { id: 'text', name: 'Text', icon: 'Type' }
  ];

  const indicators = [
    { id: 'sma', name: 'SMA', fullName: 'Simple Moving Average' },
    { id: 'ema', name: 'EMA', fullName: 'Exponential Moving Average' },
    { id: 'rsi', name: 'RSI', fullName: 'Relative Strength Index' },
    { id: 'macd', name: 'MACD', fullName: 'Moving Average Convergence Divergence' },
    { id: 'bb', name: 'Bollinger Bands', fullName: 'Bollinger Bands' },
    { id: 'stoch', name: 'Stochastic', fullName: 'Stochastic Oscillator' }
  ];

  const chartTypes = [
    { id: 'candlestick', name: 'Candlestick', icon: 'BarChart3' },
    { id: 'line', name: 'Line', icon: 'TrendingUp' },
    { id: 'area', name: 'Area', icon: 'AreaChart' },
    { id: 'bar', name: 'Bar', icon: 'BarChart' }
  ];

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev?.[section]
    }));
  };

  return (
    <div className="w-full h-full bg-card border-r border-border flex flex-col">
      {/* Drawing Tools Section */}
      <div className="border-b border-border">
        <button
          onClick={() => toggleSection('drawing')}
          className="w-full flex items-center justify-between p-3 text-sm font-medium text-foreground hover:bg-muted transition-colors"
        >
          <span>Drawing Tools</span>
          <Icon 
            name={expandedSections?.drawing ? "ChevronDown" : "ChevronRight"} 
            size={16} 
          />
        </button>
        
        {expandedSections?.drawing && (
          <div className="p-2 space-y-1">
            {drawingTools?.map((tool) => (
              <button
                key={tool?.id}
                onClick={() => onToolSelect(tool?.id)}
                className={`w-full flex items-center p-2 rounded-md text-sm transition-colors ${
                  selectedTool === tool?.id
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
                title={tool?.name}
              >
                <Icon name={tool?.icon} size={16} className="mr-2" />
                <span className="truncate">{tool?.name}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      {/* Technical Indicators Section */}
      <div className="border-b border-border">
        <button
          onClick={() => toggleSection('indicators')}
          className="w-full flex items-center justify-between p-3 text-sm font-medium text-foreground hover:bg-muted transition-colors"
        >
          <span>Indicators</span>
          <Icon 
            name={expandedSections?.indicators ? "ChevronDown" : "ChevronRight"} 
            size={16} 
          />
        </button>
        
        {expandedSections?.indicators && (
          <div className="p-2 space-y-1">
            {indicators?.map((indicator) => (
              <button
                key={indicator?.id}
                onClick={() => onIndicatorAdd(indicator?.id)}
                className="w-full flex flex-col items-start p-2 rounded-md text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                title={indicator?.fullName}
              >
                <span className="font-medium">{indicator?.name}</span>
                <span className="text-xs opacity-75 truncate w-full">{indicator?.fullName}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      {/* Chart Types Section */}
      <div className="border-b border-border">
        <button
          onClick={() => toggleSection('chartTypes')}
          className="w-full flex items-center justify-between p-3 text-sm font-medium text-foreground hover:bg-muted transition-colors"
        >
          <span>Chart Types</span>
          <Icon 
            name={expandedSections?.chartTypes ? "ChevronDown" : "ChevronRight"} 
            size={16} 
          />
        </button>
        
        {expandedSections?.chartTypes && (
          <div className="p-2 space-y-1">
            {chartTypes?.map((type) => (
              <button
                key={type?.id}
                onClick={() => onChartTypeChange(type?.id)}
                className={`w-full flex items-center p-2 rounded-md text-sm transition-colors ${
                  selectedChartType === type?.id
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
              >
                <Icon name={type?.icon} size={16} className="mr-2" />
                <span>{type?.name}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      {/* Quick Actions */}
      <div className="flex-1 p-2">
        <div className="space-y-2">
          <Button
            variant="outline"
            size="sm"
            iconName="Download"
            className="w-full justify-start"
          >
            Export Chart
          </Button>
          <Button
            variant="outline"
            size="sm"
            iconName="Camera"
            className="w-full justify-start"
          >
            Screenshot
          </Button>
          <Button
            variant="outline"
            size="sm"
            iconName="RotateCcw"
            className="w-full justify-start"
          >
            Reset View
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ChartToolbar;